const CSV_HEADERS = [
  'ID',
  'Student Code',
  'First Name',
  'Last Name',
  'Email',
  'Mobile',
  'Gender',
  'DOB',
  'Course',
  'Batch',
  'Branch',
  'Roll No',
  'Status',
  'Fee Status',
  'Guardian Name',
  'Guardian Mobile',
  'Address',
  'Admission Date',
]

const CSV_KEYS = [
  'id',
  'studentCode',
  'firstName',
  'lastName',
  'email',
  'mobile',
  'gender',
  'dob',
  'course',
  'batch',
  'branch',
  'registrationNo',
  'status',
  'feeStatus',
  'guardianName',
  'guardianMobile',
  'address',
  'admissionDate',
]

function escapeCsvCell(value) {
  const str = String(value ?? '')
  return `"${str.replace(/"/g, '""')}"`
}

export function downloadStudentsCSV(rows, filename = 'students') {
  if (!rows || rows.length === 0) return

  const headerLine = CSV_HEADERS.map(escapeCsvCell).join(',')
  const bodyLines = rows.map((row) =>
    CSV_KEYS.map((key) => escapeCsvCell(row[key])).join(','),
  )
  const csv = [headerLine, ...bodyLines].join('\n')
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
  const link = document.createElement('a')
  link.href = URL.createObjectURL(blob)
  link.download = `${filename}_${new Date().toISOString().slice(0, 10)}.csv`
  link.click()
  URL.revokeObjectURL(link.href)
}

function escapeHtml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
}

export function printStudentsReport(rows, title = 'Student Report') {
  if (!rows || rows.length === 0) return

  const printWindow = window.open('about:blank', '_blank', 'width=1200,height=850')
  if (!printWindow) return

  const header = CSV_HEADERS.map((h) => `<th>${escapeHtml(h)}</th>`).join('')
  const body = rows
    .map((row) => {
      const cells = CSV_KEYS.map((key) => `<td>${escapeHtml(row[key])}</td>`).join('')
      return `<tr>${cells}</tr>`
    })
    .join('')
  const generatedAt = new Date().toLocaleString()

  printWindow.document.open()
  printWindow.document.write(
    `<!doctype html><html><head><meta charset="utf-8" /><title>${escapeHtml(title)}</title><style>
    body{font-family:Arial,sans-serif;margin:24px;color:#191c1e}
    h1{margin:0 0 6px;font-size:24px;color:#00327d}
    p{margin:0 0 16px;color:#434653;font-size:12px}
    table{width:100%;border-collapse:collapse}
    th,td{border:1px solid #d7dce6;padding:8px;text-align:left;font-size:11px;vertical-align:top}
    th{background:#f2f4f6;font-size:11px;text-transform:uppercase;letter-spacing:.05em}
    </style></head><body>
    <h1>${escapeHtml(title)}</h1>
    <p>Generated on ${escapeHtml(generatedAt)} &mdash; ${rows.length} record(s)</p>
    <table><thead><tr>${header}</tr></thead><tbody>${body}</tbody></table>
    <script>window.onload=()=>window.print();</script>
    </body></html>`,
  )
  printWindow.document.close()
}

const PRINT_BASE_STYLE = `body{font-family:Arial,sans-serif;margin:24px;color:#191c1e}
    h1{margin:0 0 6px;font-size:24px;color:#00327d}
    p{margin:0 0 16px;color:#434653;font-size:12px}
    table{width:100%;border-collapse:collapse}
    th,td{border:1px solid #d7dce6;padding:8px;text-align:left;font-size:11px;vertical-align:top}
    th{background:#f2f4f6;font-size:11px;text-transform:uppercase;letter-spacing:.05em}`

function openPrintWindow(title, htmlBody, styleOverrides) {
  const printWindow = window.open('about:blank', '_blank', 'width=1200,height=850')
  if (!printWindow) return null
  const generatedAt = new Date().toLocaleString()
  printWindow.document.open()
  printWindow.document.write(
    `<!doctype html><html><head><meta charset="utf-8" /><title>${escapeHtml(title)}</title><style>
    ${PRINT_BASE_STYLE}
    ${styleOverrides || ''}
    </style></head><body>
    <h1>${escapeHtml(title)}</h1>
    <p>Generated on ${escapeHtml(generatedAt)}</p>
    ${htmlBody}
    <script>window.onload=()=>window.print();</script>
    </body></html>`,
  )
  printWindow.document.close()
  return printWindow
}

export function printIdCards(rows, title = 'ID Card') {
  if (!rows || rows.length === 0) return

  const cards = rows
    .map((r) => {
      const photo = r.photoUrl
        ? `<img src="${escapeHtml(r.photoUrl)}" style="width:80px;height:80px;border-radius:50%;object-fit:cover;border:2px solid #00327d" />`
        : `<div style="width:80px;height:80px;border-radius:50%;background:#e8eaf6;display:flex;align-items:center;justify-content:center;font-size:28px;font-weight:bold;color:#00327d;border:2px solid #00327d">${escapeHtml((r.firstName || '?')[0])}${escapeHtml((r.lastName || '')[0] || '')}</div>`
      return `<div style="width:260px;border:2px solid #d7dce6;border-radius:12px;padding:20px;text-align:center;break-inside:avoid;margin:10px;display:inline-block;vertical-align:top">
        <div style="margin-bottom:12px">${photo}</div>
        <p style="margin:0;font-size:16px;font-weight:bold;color:#00327d">${escapeHtml(r.firstName)} ${escapeHtml(r.lastName)}</p>
        <p style="margin:4px 0 0;font-size:11px;color:#434653">${escapeHtml(r.studentCode || '')}</p>
        <hr style="margin:10px 0;border:none;border-top:1px solid #d7dce6" />
        <p style="margin:0;font-size:12px;color:#191c1e"><strong>Course:</strong> ${escapeHtml(r.course || '—')}</p>
        <p style="margin:4px 0 0;font-size:12px;color:#191c1e"><strong>Batch:</strong> ${escapeHtml(r.batch || '—')}</p>
        ${r.branch ? `<p style="margin:4px 0 0;font-size:12px;color:#191c1e"><strong>Branch:</strong> ${escapeHtml(r.branch)}</p>` : ''}
        ${r.mobile ? `<p style="margin:4px 0 0;font-size:12px;color:#191c1e"><strong>Mobile:</strong> ${escapeHtml(r.mobile)}</p>` : ''}
      </div>`
    })
    .join('')

  openPrintWindow(title, `<div>${cards}</div>`, `.card-grid{display:flex;flex-wrap:wrap;gap:10px}`)
}

export function printStudentsWithoutPhoto(rows, title = 'List of Students Without Photograph') {
  if (!rows || rows.length === 0) return

  const header = '<th>#</th><th>Student Name</th><th>Code</th><th>Course</th><th>Batch</th><th>Mobile</th>'
  const body = rows
    .map((r, i) => `<tr>
      <td>${i + 1}</td>
      <td>${escapeHtml(r.firstName)} ${escapeHtml(r.lastName)}</td>
      <td>${escapeHtml(r.studentCode || '—')}</td>
      <td>${escapeHtml(r.course || '—')}</td>
      <td>${escapeHtml(r.batch || '—')}</td>
      <td>${escapeHtml(r.mobile || '—')}</td>
    </tr>`)
    .join('')

  openPrintWindow(title, `<p>Students without a photograph on file.</p><table><thead><tr>${header}</tr></thead><tbody>${body}</tbody></table>`)
}

export function printBatchStrength(rows, title = 'Batch Wise Strength') {
  if (!rows || rows.length === 0) return

  const batchMap = {}
  rows.forEach((r) => {
    const key = r.batch || 'Unknown'
    batchMap[key] = (batchMap[key] || 0) + 1
  })
  const sorted = Object.entries(batchMap).sort((a, b) => b[1] - a[1])
  const total = rows.length

  const header = '<th>#</th><th>Batch Name</th><th>Student Count</th><th>% of Total</th>'
  const body = sorted
    .map(([batch, count], i) => `<tr>
      <td>${i + 1}</td>
      <td>${escapeHtml(batch)}</td>
      <td>${count}</td>
      <td>${total > 0 ? ((count / total) * 100).toFixed(1) : 0}%</td>
    </tr>`)
    .join('')

  openPrintWindow(title, `<p>Total students: <strong>${total}</strong> across <strong>${sorted.length}</strong> batch(es).</p><table><thead><tr>${header}</tr></thead><tbody>${body}</tbody></table>`)
}

export function printEnvelope(rows, title = 'Envelope') {
  if (!rows || rows.length === 0) return

  const blocks = rows
    .map((r) => {
      const addressLines = [
        escapeHtml(r.firstName) + ' ' + escapeHtml(r.lastName),
        escapeHtml(r.address || ''),
        escapeHtml(r.city || ''),
        escapeHtml(r.state || ''),
        escapeHtml(r.pincode || ''),
      ].filter(Boolean)
      return `<div style="width:100%;padding:24px;border:1px dashed #d7dce6;margin-bottom:16px;break-inside:avoid">
        <p style="margin:0;font-size:13px;line-height:1.8">${addressLines.join('<br/>')}</p>
      </div>`
    })
    .join('')

  openPrintWindow(title, `<div>${blocks}</div>`, `.label-block{margin-bottom:16px}`)
}

export function printStudentLabels(rows, title = 'Label-Students') {
  if (!rows || rows.length === 0) return

  const labels = rows
    .map((r) => {
      const lines = [
        escapeHtml(r.firstName) + ' ' + escapeHtml(r.lastName),
        escapeHtml(r.address || ''),
        `${escapeHtml(r.city || '')}${r.city && r.state ? ', ' : ''}${escapeHtml(r.state || '')}`,
        escapeHtml(r.pincode || ''),
      ].filter((l) => l.trim())
      return `<div style="width:200px;height:100px;border:1px solid #d7dce6;padding:8px;display:inline-block;vertical-align:top;margin:4px;overflow:hidden;font-size:10px;line-height:1.4;break-inside:avoid">
        ${lines.join('<br/>')}
      </div>`
    })
    .join('')

  openPrintWindow(title, `<div>${labels}</div>`, `.label-grid{display:flex;flex-wrap:wrap;gap:0}`)
}

export function printParentLabels(rows, title = 'Label - Parents') {
  if (!rows || rows.length === 0) return

  const labels = rows
    .map((r) => {
      const lines = [
        escapeHtml(r.guardianName || r.firstName + ' ' + r.lastName),
        escapeHtml(r.address || ''),
        `${escapeHtml(r.city || '')}${r.city && r.state ? ', ' : ''}${escapeHtml(r.state || '')}`,
        escapeHtml(r.pincode || ''),
      ].filter((l) => l.trim())
      return `<div style="width:200px;height:100px;border:1px solid #d7dce6;padding:8px;display:inline-block;vertical-align:top;margin:4px;overflow:hidden;font-size:10px;line-height:1.4;break-inside:avoid">
        ${lines.join('<br/>')}
      </div>`
    })
    .join('')

  openPrintWindow(title, `<div>${labels}</div>`, `.label-grid{display:flex;flex-wrap:wrap;gap:0}`)
}
